import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createTranslator, supportedLocales, defaultLocale } from '../i18n';

const LanguageContext = createContext();

const STORAGE_KEY = 'dashboard_language';

export function LanguageProvider({ children }) {
  const [locale, setLocale] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || defaultLocale;
    } catch {
      return defaultLocale;
    }
  });

  const t = useCallback(createTranslator(locale), [locale]);

  const changeLanguage = useCallback((newLocale) => {
    const supported = supportedLocales.find(l => l.code === newLocale);
    if (supported) {
      setLocale(newLocale);
      try {
        localStorage.setItem(STORAGE_KEY, newLocale);
      } catch {
        // localStorage not available
      }
    }
  }, []);

  // Apply RTL direction to body when Arabic is selected
  useEffect(() => {
    const currentLocale = supportedLocales.find(l => l.code === locale);
    if (currentLocale) {
      document.documentElement.setAttribute('dir', currentLocale.dir);
      document.documentElement.setAttribute('lang', locale);
    }
  }, [locale]);

  const isRTL = locale === 'ar';

  const value = {
    locale,
    t,
    changeLanguage,
    isRTL,
    supportedLocales,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
